import { useState, useEffect, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import { FaEye, FaEyeSlash, FaCheck, FaTimes } from "../../utils/icons";
import { API } from "../../api";
import { decodeJwtPayload } from "../../utils/jwt";
import lmsAnim from "../../animations/lms.json";
import Logo from "../../assets/logo.png";

const EyeIcon = FaEye as ComponentType<any>;
const EyeSlashIcon = FaEyeSlash as ComponentType<any>;

type LoginResponse = {
  message: string;
  token: string;
};

const getTokenRole = (token: string) => {
  const payload = decodeJwtPayload(token);
  return payload?.role;
};

export default function Login() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<"login" | "forgot" | "reset">("login");
  const [form, setForm] = useState({
    email: "",
    password: "",
  });
  const [resetEmail, setResetEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showResetPass, setShowResetPass] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const lastSeen = localStorage.getItem("sessionLastSeen");
    if (token && lastSeen) {
      const elapsed = Date.now() - Number(lastSeen);
      if (elapsed < 7 * 24 * 60 * 60 * 1000) {
        const payload = decodeJwtPayload(token);
        if (payload) {
          navigate(payload.role === "admin" ? "/admin" : "/student/select-course");
        }
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("sessionLastSeen");
      }
    }
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setIsError(false);

    const email = form.email.trim();
    if (!email || !form.password) {
      setIsError(true);
      setMessage("Please enter both email and password.");
      setLoading(false);
      return;
    }

    if (!termsAccepted) {
      setIsError(true);
      setMessage("You must accept the terms and conditions.");
      setLoading(false);
      return;
    }

    try {
      const res = await API.post<LoginResponse>("/auth/login", { ...form, email });
      const token = res.data.token;
      localStorage.setItem("token", token);
      localStorage.setItem("sessionLastSeen", Date.now().toString());

      const payload = decodeJwtPayload(token);
      localStorage.setItem("userName", payload?.name || payload?.username || payload?.email || "");
      const role = getTokenRole(token);

      navigate(role === "admin" ? "/admin" : "/student/select-course");
    } catch (err: any) {
      setIsError(true);
      setMessage(err.response?.data?.error || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setIsError(false);
    try {
      await API.post("/auth/forgot-password", { email: resetEmail });
      setView("reset");
      setMessage("OTP sent to your email.");
    } catch (err: any) {
      setIsError(true);
      setMessage(err.response?.data?.error || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setIsError(false);

    if (newPassword.length > 64) {
      setIsError(true);
      setMessage("Password must be at most 64 characters.");
      setLoading(false);
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;
    if (!passwordRegex.test(newPassword)) {
      setIsError(true);
      setMessage("Password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters.");
      setLoading(false);
      return;
    }

    try {
      await API.post("/auth/reset-password", { email: resetEmail, otp, newPassword });
      setView("login");
      setMessage("Password updated successfully. Please sign in.");
    } catch (err: any) {
      setIsError(true);
      setMessage(err.response?.data?.error || "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-page">
      <style>{`
        /* Enhanced Mobile Responsiveness */
        .auth-page {
          display: flex;
          min-height: 100vh;
          width: 100%;
        }
        .auth-visual {
          flex: 1.2;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 60px;
          background: #075f65;
          color: white;
        }
        .auth-brand {
          font-size: 1.75rem;
          font-weight: 800;
          margin-bottom: auto;
          display: flex;
          align-items: center;
          gap: 12px;
          letter-spacing: -0.02em;
        }
        .auth-brand span {
          display: block;
          font-size: 1rem;
          font-weight: 400;
          opacity: 0.8;
        }
        .auth-panel {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
          background: #f8fafc;
        }
        .auth-card {
          width: 100%;
          max-width: 420px;
          background: #ffffff;
          padding: 40px;
          border-radius: 16px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          border: 1px solid #f1f5f9;
        }
        .auth-card h2 {
          font-size: 1.875rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 8px 0;
        }
        .auth-card .subtitle {
          color: #64748b;
          font-size: 0.95rem;
          margin-bottom: 32px;
        }
        .mobile-brand {
          display: none;
        }
        .mobile-brand .brand-text, .auth-brand .brand-details {
          color: #1e3a8a;
          font-size: 1.25rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          line-height: 1.2;
        }
        .auth-brand .brand-details { color: #fff; font-size: 1.75rem; }
        .mobile-brand .brand-text span, .auth-brand .brand-details span {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #64748b;
          margin-top: 2px;
        }
        .auth-brand .brand-details span { color: #fff; opacity: 0.8; font-size: 1rem; font-weight: 400; }

        .field {
          margin-bottom: 12px;
          position: relative;
        }
        .field label {
          display: block;
          font-size: 0.875rem;
          font-weight: 600;
          color: #334155;
          margin-bottom: 8px;
        }
        .field input {
          width: 100%;
          padding: 12px 16px;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          font-size: 1rem;
          color: #1e293b;
          transition: all 0.2s ease;
        }
        .field input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        .auth-submit {
          width: 100%;
          padding: 14px;
          background: #075f65 !important;
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: background 0.2s;
          margin-top: 12px;
        }
        .auth-submit:hover { background: #054d52 !important; }

        .forgot-link {
          display: block;
          text-align: right;
          margin-top: 8px;
          font-size: 0.85rem;
          color: #1e3a8a;
          cursor: pointer;
          font-weight: 600;
        }
        .forgot-link:hover {
          text-decoration: underline;
        }

        .terms-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-top: 14px;
          margin-bottom: 2px;
        }
        .terms-row input[type="checkbox"] {
          width: 17px;
          height: 17px;
          min-width: 17px;
          accent-color: #1e3a8a;
          cursor: pointer;
          margin-top: 2px;
        }
        .terms-row label {
          font-size: 0.85rem;
          color: #475569;
          cursor: pointer;
          line-height: 1.5;
        }
        .terms-row label a {
          color: #1e3a8a;
          font-weight: 600;
          text-decoration: none;
        }
        .terms-row label a:hover {
          text-decoration: underline;
        }

        .terms-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }
        .terms-modal {
          background: #fff;
          border-radius: 16px;
          width: 100%;
          max-width: 720px;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.35);
        }
        .terms-modal-header {
          padding: 24px 28px 16px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .terms-modal-header h3 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }
        .terms-modal-header button {
          background: none;
          border: none;
          font-size: 1.4rem;
          cursor: pointer;
          color: #64748b;
          line-height: 1;
          padding: 0;
        }
        .terms-modal-body {
          padding: 20px 28px;
          overflow-y: auto;
          flex: 1;
          font-size: 0.875rem;
          color: #334155;
          line-height: 1.7;
        }
        .terms-modal-body h4 {
          font-size: 0.95rem;
          font-weight: 700;
          color: #0f172a;
          margin: 18px 0 6px;
        }
        .terms-modal-body h4:first-child { margin-top: 0; }
        .terms-modal-footer {
          padding: 16px 28px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }
        .terms-accept-btn {
          padding: 10px 24px;
          background: #075f65;
          color: #fff;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
        }
        .terms-accept-btn:hover { background: #054d52; }
        .terms-close-btn {
          padding: 10px 24px;
          background: #f1f5f9;
          color: #475569;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
        }
        .terms-close-btn:hover { background: #e2e8f0; }

        @media (max-width: 768px) {
          .auth-page {
            flex-direction: column;
            min-height: 100vh;
          }
          .auth-visual {
            display: none;
          }
          .auth-panel {
            padding: 40px 16px;
            background: #075f65;
            flex-direction: column;
            justify-content: flex-start;
            padding-top: 60px;
          }
          .auth-card {
            max-width: 100%;
          }
          .mobile-brand {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 32px;
            width: 100%;
            max-width: 420px;
            /* Adjust text color for mobile brand on blue background */
            .brand-text {
              color: #fff;
            }
            .brand-text span {
              color: #fff;
              opacity: 0.8;
            }
          }
          .auth-role-strip {
            justify-content: center;
          }
        }
      `}</style>
      <section className="auth-visual">
        <div className="auth-brand">
          <img src={Logo} alt="Logo" style={{ width: '60px', height: '60px', objectFit: 'contain' }} />
          <div className="brand-details">
            Finance Education Platform
            <span>Powered By Neslife Research</span>
          </div>
        </div>
        <div style={{ maxWidth: '700px', marginBottom: '70px', marginTop: 'auto' }}>
          <Lottie animationData={lmsAnim} loop={true} />
        </div>
      </section>

      <section className="auth-panel">
        <div className="mobile-brand">
          <img src={Logo} alt="Logo" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
          <div className="brand-text">
            Finance Education Platform
            <span>Powered By Neslife Research</span>
          </div>
        </div>
        <form className="auth-card" onSubmit={view === 'login' ? handleLogin : view === 'forgot' ? handleForgotPassword : handleResetPassword}>
          {view === "login" && (
            <>
              <h2>Welcome Back</h2>
              <p className="subtitle">Please enter your registered credentials to sign in.</p>
              <div className="field-stack">
                <div className="field">
                  <label htmlFor="login-email">Email address</label>
                  <input
                    id="login-email"
                    type="email"
                    placeholder="name@example.com"
                    value={form.email}
                    required
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>

                <div className="field">
                  <label htmlFor="login-password">Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="login-password"
                      type={showPass ? "text" : "password"}
                      placeholder="Enter your password"
                      value={form.password}
                      required
                      maxLength={64}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      style={{ paddingRight: '45px' }}
                    />
                    <button 
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }}
                      aria-label={showPass ? "Hide password" : "Show password"}
                    >
                      {showPass ? <EyeSlashIcon size={18} /> : <EyeIcon size={18} />}
                    </button>
                  </div>
                  <span className="forgot-link" onClick={() => { setView("forgot"); setMessage(""); }}>
                    Forgot Password?
                  </span>
                </div>
              </div>

              <div className="terms-row">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <label htmlFor="terms-checkbox">
                  I agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    style={{ background: "none", border: "none", padding: 0, color: "#1e3a8a", fontWeight: 600, fontSize: "inherit", cursor: "pointer", textDecoration: "none" }}
                    onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")}
                    onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}
                  >
                    Terms &amp; Conditions
                  </button>
                </label>
              </div>

              {message && <p className={`form-message ${isError ? "error" : ""}`}>{message}</p>}

              <button className="primary-btn auth-submit" disabled={loading}>
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </>
          )}

          {view === "forgot" && (
            <>
              <h2>Forgot Password</h2>
              <p className="subtitle">Enter your email to receive a security code.</p>
              <div className="field">
                <label>Email address</label>
                <input
                  type="email"
                  placeholder="name@example.com"
                  value={resetEmail}
                  required
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>
              {message && <p className={`form-message ${isError ? "error" : ""}`}>{message}</p>}
              <button className="primary-btn auth-submit" disabled={loading}>
                {loading ? "Sending..." : "Send Security Code"}
              </button>
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <span onClick={() => { setView("login"); setMessage(""); }} style={{ cursor: 'pointer', color: '#1e3a8a', fontWeight: 600 }}>
                  Back to Login
                </span>
              </div>
            </>
          )}

          {view === "reset" && (
            <>
              <h2>Reset Password</h2>
              <p className="subtitle">Enter the code sent to {resetEmail}</p>
              <div className="field-stack">
                <div className="field">
                  <label>Security Code (OTP)</label>
                  <input
                    placeholder="6-digit code"
                    value={otp}
                    required
                    onChange={(e) => setOtp(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label>New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showResetPass ? "text" : "password"}
                      placeholder="8-64 characters"
                      value={newPassword}
                      required
                      maxLength={64}
                      onChange={(e) => setNewPassword(e.target.value)}
                      style={{ paddingRight: '45px' }}
                    />
                    <button 
                      type="button"
                      onClick={() => setShowResetPass(!showResetPass)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }}
                      aria-label={showResetPass ? "Hide password" : "Show password"}
                    >
                      {showResetPass ? <EyeSlashIcon size={18} /> : <EyeIcon size={18} />}
                    </button>
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <PasswordRequirement label="Uppercase letter" met={/[A-Z]/.test(newPassword)} />
                    <PasswordRequirement label="Lowercase letter" met={/[a-z]/.test(newPassword)} />
                    <PasswordRequirement label="Number" met={/[0-9]/.test(newPassword)} />
                    <PasswordRequirement label="Special character (!@#$%^&*)" met={/[!@#$%^&*]/.test(newPassword)} />
                    <PasswordRequirement label="At least 8 characters" met={newPassword.length >= 8} />
                  </div>
                </div>
              </div>
              {message && <p className={`form-message ${isError ? "error" : ""}`}>{message}</p>}
              <button className="primary-btn auth-submit" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </>
          )}
        </form>
      </section>

      {showTermsModal && (
        <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h3>Terms &amp; Conditions</h3>
              <button type="button" onClick={() => setShowTermsModal(false)} aria-label="Close">&#x2715;</button>
            </div>
            <div className="terms-modal-body">
              <h4>Disclaimer</h4>
              <p>The financial education programs conducted by Neslife Research are for educational purposes only and are intended to enhance financial knowledge. They do not constitute investment advice, stock tips, or buy/sell/hold recommendations, nor do they guarantee any returns.</p>
              <p>The educators and mentors at Neslife Research are not registered with SEBI as Investment Advisers, Research Analysts, or Brokers. The programs adhere to SEBI guidelines for educational activities and utilize market data solely for educational purposes, which may be up to one month old.</p>
              <p>Neslife Research does not sell or promote any financial products or services, and has no affiliation with brokerage firms, financial advisors, insurance companies, or other financial institutions.</p>
              <p>Participants are solely responsible for their own investment decisions and should conduct independent research. All investments are subject to market risks, and Neslife Research shall not be liable for any financial losses arising from such decisions.</p>
            </div>
            <div className="terms-modal-footer">
              <button type="button" className="terms-close-btn" onClick={() => setShowTermsModal(false)}>Close</button>
              <button
                type="button"
                className="terms-accept-btn"
                onClick={() => { setTermsAccepted(true); setShowTermsModal(false); }}
              >
                I Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function PasswordRequirement({ label, met }: { label: string; met: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: met ? '#10b981' : '#ef4444' }}>
      {met ? <FaCheck style={{ color: '#10b981' }} /> : <FaTimes style={{ color: '#ef4444' }} />}
      <span style={{ color: '#64748b' }}>{label}</span>
    </div>
  );
}
